import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';
import { IRestBaseListResponse } from '../models/rest/baseResponse';

export interface IField {
  Id: number;
  FieldName: string;
  FieldType: string;
  Length: number;
  DisplayName: string;
  IsPartOfField: boolean;
  RelatedField?: number;
  Module: string;
  IsStatic: boolean;
  IsRequired: boolean;
  Value: string;
  fkRegion: number;
  fkRuleSet: number;
  AutofillMapping: string;
}

export interface IFieldGroup {
  Id: number;
  Name: string;
  Created?: Date;
  Modified?: Date;
  fkRuleSet: number;
  FieldSetCount?: number;
}

export interface IFieldGroupWithFieldSets extends IFieldGroup {
  FieldSetIds: number[];
}

export interface IFieldGroupFieldSet {
  Id: number;
  fkFieldGroup: number;
  fkFieldSet: number;
}

export interface MappingFieldInfo {
  Key: string;
  Description: string;
}

export interface IAutofillSourcesRegistry {
  [key: string]: MappingFieldInfo[];
}

interface IAutoFillSourceResponse {
  Sources: IAutofillSourcesRegistry;
}

@Injectable({ providedIn: 'root' })
export class FieldSetService {
  constructor(private _httpClient: HttpClient) {}

  // Get all Fields
  getAll(regionId?: number, ruleSetId?: number, isStatic?: boolean): Observable<IRestBaseListResponse<IField>> {
    let params = new HttpParams();
    if (!!regionId) {
      params = params.set('fkRegion', regionId.toString());
    }
    if (!!ruleSetId) {
      params = params.set('fkRuleSet', ruleSetId.toString());
    }
    if (isStatic !== null && isStatic !== undefined) {
      params = params.set('isStatic', isStatic.toString());
    }

    if (isStatic !== null && isStatic !== undefined) {
      params = params.set('isStatic', isStatic.toString());
    }

    return this._httpClient.get<IRestBaseListResponse<IField>>('FieldSet/', { params: params });
  }

  // Get all Field Groups (fkRuleSet is required)
  getGroups(ruleSetId: number): Observable<IRestBaseListResponse<IFieldGroup>> {
    let params = new HttpParams();
    params = params.set('fkRuleSet', ruleSetId.toString());

    return this._httpClient.get<IRestBaseListResponse<IFieldGroup>>('FieldGroup/', { params: params });
  }

  // Get all Field Groups with linked FieldSet ids (fkRuleSet is required)
  getGroupsWithFieldSets(ruleSetId: number): Observable<IRestBaseListResponse<IFieldGroupWithFieldSets>> {
    let params = new HttpParams();
    params = params.set('fkRuleSet', ruleSetId.toString());

    return this._httpClient.get<IRestBaseListResponse<IFieldGroupWithFieldSets>>('FieldGroup/WithFieldSets', { params: params });
  }

  // Create or Update Field Group
  createOrUpdateGroup(group: IFieldGroup): Observable<any> {
    return this._httpClient.put<any>('FieldGroup/', { FieldGroup: group });
  }

  // Delete Field Group
  deleteGroup(groupId: number): Observable<any> {
    return this._httpClient.delete<any>('FieldGroup/' + groupId);
  }

  // Get links between Field Group and FieldSet
  getGroupFieldSets(fieldGroupId: number): Observable<IRestBaseListResponse<IFieldGroupFieldSet>> {
    return this._httpClient.get<IRestBaseListResponse<IFieldGroupFieldSet>>(`FieldGroup/${fieldGroupId}/FieldSet`);
  }

  // Save links between Field Group and FieldSet
  saveGroupFieldSets(fieldGroupId: number, fieldSetIds: number[]): Observable<any> {
    return this._httpClient.post<any>(`FieldGroup/${fieldGroupId}/FieldSet`, { FieldSetIds: fieldSetIds });
  }

  // Get a Field
  get(fieldId: number): Observable<any> {
    return this._httpClient.get<any>('FieldSet/' + fieldId);
  }

  // Create a Field
  create(field: any): Observable<any> {
    return this._httpClient.put<any>('FieldSet/', { field });
  }

  // Update a Field
  update(field: any): Observable<any> {
    return this._httpClient.put<any>('FieldSet/', { field });
  }

  // Delete a Field
  delete(fieldId: number): Observable<any> {
    return this._httpClient.delete<any>('FieldSet/' + fieldId);
  }

  getAutoFillingSources(): Observable<IAutoFillSourceResponse> {
    return this._httpClient.get<any>('Autofilling/Sources');
  }

  resetCache() {}
}
